<template>
  <div id="cost-summary-pivot-table">
    <pivot-table :data="data" class="pivot-table"
                 :fields="fields"
                 :row-fields="rowFields"
                 :col-fields="colFields"
                 :reducer="reducer"
                 :default-show-settings="defaultShowSettings"
                 :is-data-loading="loading">
      <template slot="value" slot-scope="{ value, row, col }">
        <Row :gutter="12">
          <Col span="4" align="right">{{intlCurrencyNumber(currency, _.get(value, 'scrap_amount'), 2, 2)}}</Col>
          <Col span="4" align="right">{{intlCurrencyNumber(currency, _.get(value, 'price_amount'), 2, 2)}}</Col>
          <Col span="4" align="right">{{intlCurrencyNumber(currency, _.get(value, 'vat_amount'), 2, 2)}}</Col>
          <Col span="4" align="right">{{intlCurrencyNumber(currency, _.get(value, 'price_amount_taxed'), 2, 2)}}</Col>
          <Col span="3" align="right">{{intlCurrencyNumber(currency, _.get(value, 'price_amount') / partCost.quantity, 2, 2)}}</Col>
          <Col span="3" align="right">{{intlCurrencyNumber(currency, _.get(value, 'price_amount_taxed') / partCost.quantity, 2, 2)}}</Col>
          <Col span="2"><Button type="text" @click="handleShowDetail(row, col, value)" icon="md-eye"></Button></Col>
        </Row>
      </template>
      <template slot="costHeader" slot-scope="{ value }">
        <Row :gutter="12">
          <Col span="4" align="right">{{ $t('cost.cost_summary.scrap_amount') }}</Col>
          <Col span="4" align="right">{{ $t('cost.cost_summary.total_cost') }}</Col>
          <Col span="4" align="right">{{ $t('cost.cost_summary.tax') }}</Col>
          <Col span="4" align="right">{{ $t('cost.cost_summary.amount_taxed') }}</Col>
          <Col span="3" align="right">{{ $t('cost.cost_summary.unit_cost') }}</Col>
          <Col span="3" align="right">{{ $t('cost.cost_summary.unit_cost_taxed') }}</Col>
          <Col span="2"></Col>
        </Row>
      </template>
      <template slot="rowHeader" slot-scope="{ value }">
        {{termLabel(value)}}
      </template>
      <template slot="loading">Loading...</template>
      <template slot="total" slot-scope="{ value }" class="text-right">
        <Row :gutter="12">
          <Col span="4" align="right">{{ intlCurrencyNumber(currency, colTotal('scrap_amount'), 2, 2) }}</Col>
          <Col span="4" align="right">{{ intlCurrencyNumber(currency, colTotal('price_amount'), 2, 2) }}</Col>
          <Col span="4" align="right">{{ intlCurrencyNumber(currency, colTotal('vat_amount'), 2, 2) }}</Col>
          <Col span="4" align="right">{{ intlCurrencyNumber(currency, colTotal('price_amount_taxed'), 2, 2) }}</Col>
          <Col span="3" align="right">{{ intlCurrencyNumber(currency, colTotal('price_amount')/ partCost.quantity, 2, 2) }}</Col>
          <Col span="3" align="right">{{ intlCurrencyNumber(currency, colTotal('price_amount_taxed')/ partCost.quantity, 2, 2) }}</Col>
          <Col span="2">&nbsp;</Col>
        </Row>
      </template>
    </pivot-table>
    <Modal
      v-model="showDetail"
      title=""
      width="70%"
      footer-hide
      :transfer="true"
      :z-index="1001"
      >
        <CostSummaryDetail :partCost="partCost" :data="data" :volumes="volumes" :filters="detailFilters" :currency="currency"></CostSummaryDetail>
    </Modal>
  </div>
</template>

<script>
import PivotTable from '@marketconnect/vue-pivot-table'
import CostSummaryDetail from './cost-summay-detail'
import { intlCurrencyNumber } from '@/libs/util'
export default {
  name: 'CostSummaryPivot',
  components: { PivotTable, CostSummaryDetail },
  props: {
    data: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    currency: {
      type: String,
      required: true
    },
    partCost: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      asyncData: [],
      fields: [
        {
          getter: item => item.tags,
          label: 'Tags'
        }
      ],
      showDetail: false,
      detailFilters: {},
      rowFields: [
        {
          getter: item => item.account_term.slug,
          label: 'N-R/R',
          headerSlotName: 'rowHeader'
        },
        {
          getter: item => item.associated_term.slug,
          label: 'Production/BOM',
          headerSlotName: 'rowHeader'
        },
        {
          getter: item => item.summary_term.slug,
          label: 'Summary',
          headerSlotName: 'rowHeader'
        }
      ],
      colFields: [
        {
          getter: item => 'Cost & Price',
          key: 'cost_amount',
          label: 'Total cost',
          showFooter: true,
          footerSlotName: 'total',
          headerSlotName: 'costHeader'
        }
      ],
      reducer: (sum, item) => {
        if (!sum) {
          sum = {
            vat_amount: 0,
            cost_amount: 0,
            price_amount: 0,
            price_amount_taxed: 0,
            scrap_amount: 0
          }
        }
        sum.vat_amount += item.vat_amount
        sum.cost_amount += item.cost_amount
        sum.price_amount += item.price_amount
        sum.price_amount_taxed += item.price_amount_taxed
        sum.scrap_amount += item.scrap_amount
        return sum
      },
      defaultShowSettings: false,
      isDataLoading: false
    }
  },
  computed: {
    terms: function () {
      let termsMap = {}
      _.each(this.data, (item) => {
        termsMap[item.account_term.slug] = item.account_term
        termsMap[item.account_term.slug].field = 'account_term'
        termsMap[item.account_term.slug].id_field = 'accounting_term_id'
        termsMap[item.associated_term.slug] = item.associated_term
        termsMap[item.associated_term.slug].field = 'associated_term'
        termsMap[item.associated_term.slug].id_field = 'associated_term_id'
        termsMap[item.summary_term.slug] = item.summary_term
        termsMap[item.summary_term.slug].field = 'summary_term'
        termsMap[item.summary_term.slug].id_field = 'summary_term_id'
      })
      return termsMap
    },
    volumes: function() {
      return [this.partCost.quantity]
      // let volumes = _.map(this.data, (item) => {
      //   return item.quantity
      // })
      // return _.uniq(volumes)
    }
  },
  methods: {
    intlCurrencyNumber,
    colTotal: function (totalField) {
      let values = this.data
      return values.reduce(function (carrier, value) {
        return carrier + _.get(value, totalField) * 1
      }, 0)
    },
    getTerm(slug) {
      return _.get(this.terms, slug)
    },
    termLabel: function (slug) {
      const term = this.getTerm(slug)
      return term ? _.get(term, 'label', slug) : slug
    },
    handleShowDetail (terms, volumes, value) {
      this.detailFilters = {}
      _.each(terms, (slug) => {
        let findTerm = this.getTerm(slug)
        if (findTerm) {
          this.$set(this.detailFilters, findTerm.field, findTerm)
          this.$set(this.detailFilters, `${findTerm.id_field}`, findTerm.id)
        }
      })
      this.showDetail = true
    }
  }
}
</script>
<style lang="less" scoped>

</style>
